/* eslint-disable @typescript-eslint/no-unused-vars */
import {
  Fab,
  Link,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import { deSSMLDocAtom, enSSMLDocAtom, fetchSSMLDocsAtom } from "atoms";
import { useAtomValue, useSetAtom } from "jotai";
import { Children, useEffect, useState } from "react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import rehypeSlug from "rehype-slug";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import { useTranslation } from "react-i18next";

interface MarkdownRendererProps {
  selectedLanguage: string;
}

interface Heading {
  id: string;
  text: string;
}

export default function SSMLDocRenderer({
  selectedLanguage,
}: MarkdownRendererProps): JSX.Element {
  const fetchSSMLDocs = useSetAtom(fetchSSMLDocsAtom);
  const deSSMLDoc = useAtomValue(deSSMLDocAtom);
  const enSSMLDoc = useAtomValue(enSSMLDocAtom);
  const [markdownContent, setMarkdownContent] = useState<string>("");
  const [headings, setHeadings] = useState<Heading[]>([]);
  const [showBackToTop, setShowBackToTop] = useState(false);
  const { t } = useTranslation();

  const extractHeadings = (content: string) => {
    const regex = /^##\s(.+)/gm;
    const matches = [...content.matchAll(regex)];

    const extractedHeadings = matches.map((match) => {
      let text = match[1].trim();
      text = text.replace(/`/g, "");
      const id = text.toLowerCase().replace(/\s+/g, "-");
      return { id, text };
    });

    setHeadings(extractedHeadings);
  };

  useEffect(() => {
    const handleScroll = () => {
      setShowBackToTop(window.scrollY > 300);
    };

    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  useEffect(() => {
    fetchSSMLDocs();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const content = selectedLanguage === "en" ? enSSMLDoc : deSSMLDoc;
    setMarkdownContent(content);
    extractHeadings(content);
  }, [deSSMLDoc, enSSMLDoc, selectedLanguage]);

  return (
    <Stack p={2} spacing={2}>
      {headings.length > 0 && (
        <Stack spacing={1} sx={{ borderBottom: "1px solid #ddd", pb: 2 }}>
          <Typography variant="h6">{t("ssml.toc")}</Typography>
          {headings.map((heading) => (
            <Link
              key={heading.id}
              href={`#${heading.id}`}
              color="primary"
              sx={{
                "&:hover": {
                  color: "primary.main",
                  textDecorationColor: "primary.main",
                },
              }}
            >
              {heading.text}
            </Link>
          ))}
        </Stack>
      )}

      <Stack
        p={1}
        sx={{
          "& pre code": {
            whiteSpace: "pre-wrap",
            wordWrap: "break-word",
            overflowWrap: "break-word",
            display: "block",
            backgroundColor: "#f5f5f5",
            padding: "8px",
            borderRadius: "4px",
            fontFamily: "monospace",
            fontSize: "0.875rem",
            boxShadow: "inset 0 0 5px rgba(0, 0, 0, 0.1)",
          },
        }}
      >
        <ReactMarkdown
          children={markdownContent}
          remarkPlugins={[remarkGfm]}
          rehypePlugins={[rehypeSlug]}
          components={{
            h2: ({ node, children }) => {
              if (!children) return null;
              const text = Children.toArray(children)
                .map((child) =>
                  typeof child === "string"
                    ? child
                    : // eslint-disable-next-line @typescript-eslint/no-explicit-any
                      (child as any).props?.children || ""
                )
                .join("");

              const id = text
                .toLowerCase()
                .replace(/\s+/g, "-")
                .replace(/`/g, "");

              return (
                <h2 id={id} style={{ scrollMarginTop: "64px" }}>
                  {children}
                </h2>
              );
            },
            table: ({ node, children }) => (
              <Table sx={{ minWidth: 650, border: "1px solid #ddd" }}>
                {children}
              </Table>
            ),

            thead: ({ node, children }) => (
              <TableHead sx={{ backgroundColor: "#f5f5f5" }}>
                {children}
              </TableHead>
            ),

            tbody: ({ node, children }) => <TableBody>{children}</TableBody>,

            tr: ({ node, children }) => <TableRow>{children}</TableRow>,

            th: ({ node, children }) => (
              <TableCell
                sx={{
                  fontWeight: "bold",
                  padding: "8px",
                  border: "1px solid #ddd",
                }}
              >
                {children}
              </TableCell>
            ),

            td: ({ node, children }) => (
              <TableCell sx={{ padding: "8px", border: "1px solid #ddd" }}>
                {children}
              </TableCell>
            ),
          }}
        />
      </Stack>
      {showBackToTop && (
        <Fab
          color="primary"
          size="small"
          sx={{
            position: "fixed",
            bottom: 20,
            right: 20,
          }}
          onClick={scrollToTop}
        >
          <KeyboardArrowUpIcon />
        </Fab>
      )}
    </Stack>
  );
}
